/**
 * GCSA 官网页脚 — 固化文案与绝对链接（完全复刻用）
 * 主站：https://gcsa.org · 媒体：https://media.gcsa.org
 *
 * 导航顺序与官网页脚一致：首页 → 生态 → 媒体 → 动态 → AI 研究所 → 战略合作 → 加入 → 关于
 */

export const GCSA_MAIN_ORIGIN = 'https://gcsa.org';
export const GCSA_MEDIA_ORIGIN = 'https://media.gcsa.org';
export const GCSA_CONTACT_EMAIL = 'contact@gcsa.org';
export const GCSA_X_URL = 'https://x.com/gcsa_org';
export const GCSA_TELEGRAM_URL = 'https://t.me/GCSAofficial';

export type SiteFooterLocale = 'zh-CN' | 'zh-TW' | 'en-US';

export type SiteFooterNavIcon =
  | 'home'
  | 'products'
  | 'media'
  | 'forums'
  | 'aiResearchInstitute'
  | 'strategicPartners'
  | 'contact'
  | 'about';

export type SiteFooterNavLink = {
  key: string;
  label: string;
  href: string;
  icon: SiteFooterNavIcon;
  external?: boolean;
};

export type SiteFooterSocialLink = {
  id: string;
  label: string;
  href?: string;
  icon?: string;
};

export type SiteFooterLegalLink = {
  label: string;
  href: string;
  external?: boolean;
};

export type GcsaFooterSnapshot = {
  title: string;
  email: string;
  emailLabel: string;
  copyright: string;
  navAriaLabel: string;
  legalAriaLabel: string;
  socialAriaLabel: string;
  socialPendingLabel: string;
  navLinks: SiteFooterNavLink[];
  socialLinks: SiteFooterSocialLink[];
  legalLinks: SiteFooterLegalLink[];
};

const SHARED_HREFS = {
  home: `${GCSA_MAIN_ORIGIN}/`,
  products: `${GCSA_MAIN_ORIGIN}/products`,
  media: GCSA_MEDIA_ORIGIN,
  forums: `${GCSA_MAIN_ORIGIN}/forums`,
  aiResearchInstitute: `${GCSA_MAIN_ORIGIN}/ai-research-institute`,
  strategicPartners: `${GCSA_MAIN_ORIGIN}/strategic-partners`,
  contact: `${GCSA_MAIN_ORIGIN}/contact`,
  about: `${GCSA_MAIN_ORIGIN}/about`,
  privacy: `${GCSA_MAIN_ORIGIN}/privacy-policy`,
  terms: `${GCSA_MAIN_ORIGIN}/terms-of-use`,
  cookies: `${GCSA_MAIN_ORIGIN}/cookie-settings`,
  mailto: `mailto:${GCSA_CONTACT_EMAIL}`
} as const;

const FOOTER_NAV_ORDER: SiteFooterNavIcon[] = [
  'home',
  'products',
  'media',
  'forums',
  'aiResearchInstitute',
  'strategicPartners',
  'contact',
  'about'
];

function buildNavLinks(
  labels: Record<SiteFooterNavIcon, string>,
  hrefs: Record<SiteFooterNavIcon, string> = {
    home: SHARED_HREFS.home,
    products: SHARED_HREFS.products,
    media: SHARED_HREFS.media,
    forums: SHARED_HREFS.forums,
    aiResearchInstitute: SHARED_HREFS.aiResearchInstitute,
    strategicPartners: SHARED_HREFS.strategicPartners,
    contact: SHARED_HREFS.contact,
    about: SHARED_HREFS.about
  }
): SiteFooterNavLink[] {
  return FOOTER_NAV_ORDER.map(key => ({
    key,
    label: labels[key],
    href: hrefs[key],
    icon: key,
    external: true
  }));
}

/** 简体中文 — 与站内 zh-CN 页脚一致 */
export const GCSA_FOOTER_ZH_CN: GcsaFooterSnapshot = {
  title: 'GCSA 联系方式',
  email: GCSA_CONTACT_EMAIL,
  emailLabel: '联系邮箱',
  copyright: '© Global Cybersecurity Alliance. 保留所有权利。',
  navAriaLabel: '页面导航',
  legalAriaLabel: '法律信息',
  socialAriaLabel: '社交媒体',
  socialPendingLabel: '即将开放',
  navLinks: buildNavLinks({
    home: '首页',
    products: 'GCSA 生态产品',
    media: 'GCSA 媒体',
    forums: 'GCSA 动态',
    aiResearchInstitute: 'GCSA AI网络安全研究中心',
    strategicPartners: 'GCSA 战略合作及顾问',
    contact: '加入联盟',
    about: '关于联盟'
  }),
  socialLinks: [
    { id: 'x', label: '推特', href: GCSA_X_URL },
    { id: 'telegram', label: 'Telegram', href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: '隐私政策', href: SHARED_HREFS.privacy, external: true },
    { label: '使用条款', href: SHARED_HREFS.terms, external: true },
    { label: 'Cookie 设置', href: SHARED_HREFS.cookies, external: true },
    { label: GCSA_CONTACT_EMAIL, href: SHARED_HREFS.mailto, external: true }
  ]
};

/** 繁體中文 — 与站内 zh-TW 页脚一致 */
export const GCSA_FOOTER_ZH_TW: GcsaFooterSnapshot = {
  title: 'GCSA 聯繫方式',
  email: GCSA_CONTACT_EMAIL,
  emailLabel: '聯繫郵箱',
  copyright: '© Global Cybersecurity Alliance. 保留所有權利。',
  navAriaLabel: '頁面導航',
  legalAriaLabel: '法律信息',
  socialAriaLabel: '社交媒體',
  socialPendingLabel: '即將開放',
  navLinks: buildNavLinks({
    home: '首頁',
    products: 'GCSA 生態產品',
    media: 'GCSA 媒體',
    forums: 'GCSA 動態',
    aiResearchInstitute: 'GCSA AI網絡安全研究中心',
    strategicPartners: 'GCSA 戰略合作及顧問',
    contact: '加入聯盟',
    about: '關於聯盟'
  }),
  socialLinks: [
    { id: 'x', label: '推特', href: GCSA_X_URL },
    { id: 'telegram', label: 'Telegram', href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    { label: '隱私政策', href: SHARED_HREFS.privacy, external: true },
    { label: '使用條款', href: SHARED_HREFS.terms, external: true },
    { label: 'Cookie 設置', href: SHARED_HREFS.cookies, external: true },
    { label: GCSA_CONTACT_EMAIL, href: SHARED_HREFS.mailto, external: true }
  ]
};

/** English — 与站内 en-US 页脚一致 */
export const GCSA_FOOTER_EN_US: GcsaFooterSnapshot = {
  title: 'GCSA Contact',
  email: GCSA_CONTACT_EMAIL,
  emailLabel: 'Email',
  copyright: '© Global Cybersecurity Alliance. All rights reserved.',
  navAriaLabel: 'Navigation',
  legalAriaLabel: 'Legal',
  socialAriaLabel: 'Social media',
  socialPendingLabel: 'Coming soon',
  navLinks: buildNavLinks(
    {
      home: 'Home',
      products: 'GCSA Ecosystem Products',
      media: 'GCSA Media',
      forums: 'GCSA Updates',
      aiResearchInstitute: 'GCSA AI Cybersecurity Research Institute',
      strategicPartners: 'GCSA Strategic Partners & Advisors',
      contact: 'Join the Alliance',
      about: 'About Alliance'
    },
    {
      home: `${GCSA_MAIN_ORIGIN}/en-US/`,
      products: `${GCSA_MAIN_ORIGIN}/en-US/products`,
      media: SHARED_HREFS.media,
      forums: `${GCSA_MAIN_ORIGIN}/en-US/forums`,
      aiResearchInstitute: `${GCSA_MAIN_ORIGIN}/en-US/ai-research-institute`,
      strategicPartners: `${GCSA_MAIN_ORIGIN}/en-US/strategic-partners`,
      contact: `${GCSA_MAIN_ORIGIN}/en-US/contact`,
      about: `${GCSA_MAIN_ORIGIN}/en-US/about`
    }
  ),
  socialLinks: [
    { id: 'x', label: 'Twitter', href: GCSA_X_URL },
    { id: 'telegram', label: 'Telegram', href: GCSA_TELEGRAM_URL, icon: 'fa-telegram' }
  ],
  legalLinks: [
    {
      label: 'Privacy Policy',
      href: `${GCSA_MAIN_ORIGIN}/en-US/privacy-policy`,
      external: true
    },
    {
      label: 'Terms of Use',
      href: `${GCSA_MAIN_ORIGIN}/en-US/terms-of-use`,
      external: true
    },
    {
      label: 'Cookie Settings',
      href: `${GCSA_MAIN_ORIGIN}/en-US/cookie-settings`,
      external: true
    },
    { label: GCSA_CONTACT_EMAIL, href: SHARED_HREFS.mailto, external: true }
  ]
};

export const GCSA_FOOTER_BY_LOCALE: Record<SiteFooterLocale, GcsaFooterSnapshot> = {
  'zh-CN': GCSA_FOOTER_ZH_CN,
  'zh-TW': GCSA_FOOTER_ZH_TW,
  'en-US': GCSA_FOOTER_EN_US
};

/** 默认：简体中文完整复刻 */
export const GCSA_FOOTER_DEFAULT = GCSA_FOOTER_ZH_CN;

export function resolveGcsaFooter(locale: SiteFooterLocale = 'zh-CN'): GcsaFooterSnapshot {
  return GCSA_FOOTER_BY_LOCALE[locale] ?? GCSA_FOOTER_ZH_CN;
}
